import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  TrophyIcon, 
  ShoppingBagIcon,
  ChartBarIcon,
  UserIcon,
  AcademicCapIcon,
  DocumentTextIcon,
  TrashIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { toProperCase } from '../utils/formatUtils';

const Dashboard = () => {
  const { userProfile, currentUser } = useAuth();
  const [registrations, setRegistrations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [programSignups, setProgramSignups] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedInvoices, setExpandedInvoices] = useState(new Set());
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [signupToCancel, setSignupToCancel] = useState(null);
  const [payingRegistrationId, setPayingRegistrationId] = useState(null);

  const fetchUserRegistrations = useCallback(async () => {
    try {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }
      
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const response = await fetch(`${apiUrl}/api/competitions/my-registrations`, {
        headers: {
          'Authorization': `Bearer ${await currentUser.getIdToken()}`
        }
      }).catch(error => {
        console.error('Dashboard registrations API call failed:', error);
        throw error;
      });

      if (response.ok) {
        const data = await response.json();
        setRegistrations(data.registrations || []);
      } else {
        console.error('Dashboard registrations API response not ok:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching user registrations:', error);
    }
  }, [currentUser]);

  const fetchUserOrders = useCallback(async () => {
    try {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const response = await fetch(`${apiUrl}/api/products/my-orders`, {
        headers: {
          'Authorization': `Bearer ${await currentUser.getIdToken()}`
        }
      }).catch(error => {
        console.error('Dashboard orders API call failed:', error);
        throw error;
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Orders data received:', data.orders);
        setOrders(data.orders || []);
      } else {
        console.error('Dashboard orders API response not ok:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching user orders:', error);
    }
  }, [currentUser]);

  const fetchProgramSignups = useCallback(async () => {
    try {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }
      
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const response = await fetch(`${apiUrl}/api/programs/my-signups`, {
        headers: {
          'Authorization': `Bearer ${await currentUser.getIdToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Program signups data:', data);
        console.log('Program signups count:', data.signups?.length || 0);
        setProgramSignups(data.signups || []);
      } else {
        console.error('Dashboard program signups API response not ok:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching program signups:', error);
    }
  }, [currentUser]);

  const fetchInvoices = useCallback(async () => {
    try {
      if (!currentUser) {
        throw new Error('User not authenticated');
      }
      
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const response = await fetch(`${apiUrl}/api/programs/invoices`, {
        headers: {
          'Authorization': `Bearer ${await currentUser.getIdToken()}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices || []);
      } else {
        console.error('Dashboard invoices API response not ok:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  }, [currentUser]);

  // Check if a child has unpaid invoices
  const hasUnpaidInvoices = useCallback((participantName) => {
    return invoices.some(invoice => {
      if (invoice.status === 'unpaid' && invoice.children_details) {
        return invoice.children_details.some(child => 
          child.participant_name === participantName
        );
      }
      return false;
    });
  }, [invoices]);

  const handleToggleSignupStatus = async (signup) => {
    // Don't allow toggle if cancelled
    if (signup.status === 'cancelled') {
      alert('Cannot toggle a cancelled signup. Please contact admin.');
      return;
    }
    
    // Check for unpaid invoices
    if (hasUnpaidInvoices(signup.participant_name)) {
      alert(`Cannot toggle signup - ${signup.participant_name} has outstanding invoices. Please pay all outstanding invoices first.`);
      return;
    }
    
    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      
      const response = await fetch(`${apiUrl}/api/programs/signup/${signup.id}/toggle-status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        fetchProgramSignups(); // Refresh the list
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to toggle signup status');
      }
    } catch (error) {
      console.error('Error toggling signup status:', error);
      alert('Failed to toggle signup status. Please try again.');
    }
  };

  const handleCancelSignup = (signup) => {
    // Don't allow cancelling if already cancelled
    if (signup.status === 'cancelled') {
      alert('This signup is already cancelled');
      return;
    }
    
    // Check for unpaid invoices
    if (hasUnpaidInvoices(signup.participant_name)) {
      alert(`Cannot cancel signup - ${signup.participant_name} has outstanding invoices. Please pay all outstanding invoices first.`);
      return;
    }
    
    setSignupToCancel(signup);
    setShowCancelModal(true);
  };

  const confirmCancelSignup = async () => {
    if (!signupToCancel) return;
    
    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      
      const response = await fetch(`${apiUrl}/api/programs/signup/${signupToCancel.id}/cancel`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        alert('✓ Signup cancelled successfully');
        fetchProgramSignups(); // Refresh the list
        setShowCancelModal(false);
        setSignupToCancel(null);
      } else {
        const data = await response.json();
        alert(`Failed to cancel signup: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error cancelling signup:', error);
      alert('Failed to cancel signup. Please try again.');
    }
  };

  const handlePayInvoice = async (invoiceId) => {
    try {
      if (!currentUser) {
        alert('Please login to continue');
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const response = await fetch(`${apiUrl}/api/programs/invoices/${invoiceId}/pay`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await currentUser.getIdToken()}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.payment_url) {
        // Redirect to payment page
        window.location.href = data.payment_url;
      } else {
        alert(data.error || 'Failed to initiate payment');
      }
    } catch (error) {
      console.error('Error initiating payment:', error);
      alert('Failed to initiate payment. Please try again.');
    }
  };

  const handlePayCompetitionRegistration = async (registration) => {
    if (!registration.registration_id || registration.registration_status !== 'pending_payment') return;
    if (!currentUser) {
      alert('Please login to continue');
      return;
    }
    setPayingRegistrationId(registration.registration_id);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const response = await fetch(
        `${apiUrl}/api/competitions/registrations/${registration.registration_id}/create-payment`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${await currentUser.getIdToken()}`,
            'Content-Type': 'application/json'
          }
        }
      );
      const data = await response.json();
      if (response.ok && data.payment_url) {
        window.location.href = data.payment_url;
      } else {
        alert(data.error || 'Failed to create payment. Please try again.');
      }
    } catch (error) {
      console.error('Error creating competition payment:', error);
      alert('Failed to create payment. Please try again.');
    } finally {
      setPayingRegistrationId(null);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchUserRegistrations(),
        fetchUserOrders(),
        fetchProgramSignups(),
        fetchInvoices()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchUserRegistrations, fetchUserOrders, fetchProgramSignups, fetchInvoices]);

  const quickActions = [
    {
      name: 'Browse Competitions',
      description: 'View and register for upcoming events',
      href: '/competitions',
      icon: TrophyIcon,
      color: 'bg-blue-500'
    },
    {
      name: 'Shop Merchandise',
      description: 'Browse club merchandise and gear',
      href: '/merchandise',
      icon: ShoppingBagIcon,
      color: 'bg-purple-500'
    },
    {
      name: 'View Programs',
      description: 'Check training programs and schedules',
      href: '/programmes',
      icon: ChartBarIcon,
      color: 'bg-orange-500'
    },
    {
      name: 'Update Profile',
      description: 'Manage your personal information',
      href: '/profile',
      icon: UserIcon,
      color: 'bg-green-500'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-gray-900 via-secondary-900 to-primary-900">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Welcome back, {userProfile?.first_name && userProfile?.last_name 
                ? `${userProfile.first_name} ${userProfile.last_name}` 
                : userProfile?.first_name || 'Athlete'}!
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Track your competitions, manage your orders, and stay updated with your athletic journey.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Purchase Summary */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Purchase Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <TrophyIcon className="h-8 w-8 text-primary-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-700 truncate">
                      Competition Registrations
                    </dt>
                    <dd className="text-lg font-bold text-gray-900">
                      {registrations.length}
                    </dd>
                    <dd className="text-sm text-gray-600">
                      Total Fees: ${registrations.reduce((sum, reg) => sum + (reg.total_fee_paid || 0), 0).toFixed(2)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ShoppingBagIcon className="h-8 w-8 text-primary-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-700 truncate">
                      Merchandise Orders
                    </dt>
                    <dd className="text-lg font-bold text-gray-900">
                      {orders.length}
                    </dd>
                    <dd className="text-sm text-gray-600">
                      Total Spent: ${orders.reduce((sum, order) => sum + (order.total_price || 0), 0).toFixed(2)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ChartBarIcon className="h-8 w-8 text-primary-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-700 truncate">
                      Total Investment
                    </dt>
                    <dd className="text-lg font-bold text-gray-900">
                      ${(registrations.reduce((sum, reg) => sum + (reg.total_fee_paid || 0), 0) + 
                          orders.reduce((sum, order) => sum + (order.total_price || 0), 0)).toFixed(2)}
                    </dd>
                    <dd className="text-sm text-gray-600">
                      In athletics & merchandise
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickActions.map((action) => (
              <Link
                key={action.name}
                to={action.href}
                className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg hover:shadow-xl transition-all duration-300 block hover:-translate-y-1"
              >
                <div className="flex items-center">
                  <div className={`flex-shrink-0 ${action.color} rounded-lg p-3`}>
                    <action.icon className="h-6 w-6 text-white" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      {action.name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {action.description}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Recent Registrations */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">My Competition Registrations</h2>
          {registrations.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {registrations.slice(0, 6).map((registration) => (
                <div key={registration.id} className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {registration.name}
                      </h3>
                      <div className="space-y-1 text-sm text-gray-600">
                        <p><strong>Location:</strong> {registration.location}</p>
                        <p><strong>Date:</strong> {new Date(registration.start_date).toLocaleDateString()}</p>
                        <p><strong>Events:</strong> {registration.events_registered?.map(e => toProperCase(e.event_name || e.event_code || '')).join(', ') || 'N/A'}</p>
                        <p><strong>Fee:</strong> ${registration.total_fee ?? registration.total_fee_paid ?? 0}</p>
                        <p><strong>Status:</strong>
                          <span className={`ml-1 px-2 py-1 rounded-full text-xs font-medium ${
                            registration.registration_status === 'confirmed' ? 'bg-green-100 text-green-800' :
                            registration.registration_status === 'pending_payment' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {registration.registration_status === 'confirmed' ? 'Confirmed' :
                             registration.registration_status === 'pending_payment' ? 'Pending Payment' :
                             registration.registration_status ?? '—'}
                          </span>
                        </p>
                        {registration.registration_status === 'pending_payment' && (
                          <button
                            onClick={() => handlePayCompetitionRegistration(registration)}
                            disabled={payingRegistrationId === registration.registration_id}
                            className="mt-2 w-full py-2 px-4 bg-oldham-gold hover:bg-oldham-gold/90 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {payingRegistrationId === registration.registration_id ? 'Redirecting...' : 'Complete Payment'}
                          </button>
                        )}
                        <Link
                          to="/competitions"
                          className="mt-2 inline-flex w-full items-center justify-center py-2 px-4 border border-primary-600 text-primary-700 hover:bg-primary-50 text-sm font-medium rounded-lg transition-colors"
                        >
                          Edit entries
                        </Link>
                      </div>
                    </div>
                    <div className="ml-4">
                      <TrophyIcon className="h-8 w-8 text-yellow-500" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card p-8 text-center">
              <TrophyIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Registrations Yet</h3>
              <p className="text-gray-600 mb-4">You haven't registered for any competitions yet.</p>
              <Link 
                to="/competitions" 
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-300"
              >
                Browse Competitions
              </Link>
            </div>
          )}
        </div>

        {/* Recent Orders */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">My Program Registrations</h2>
          {programSignups.filter(s => s.status !== 'cancelled').length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {programSignups.filter(s => s.status !== 'cancelled').map((signup) => (
                <div key={signup.id} className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {signup.program_name || 'Program'}
                      </h3>
                      <div className="space-y-1 text-sm text-gray-600">
                        <p><strong>Participant:</strong> {signup.participant_name}</p>
                        <p><strong>Age:</strong> {signup.participant_age} years old</p>
                        <p><strong>Fee Type:</strong> {signup.fee_type}</p>
                        <p><strong>Amount:</strong> ${signup.amount}</p>
                        <p><strong>Status:</strong> 
                          <button
                            onClick={() => handleToggleSignupStatus(signup)}
                            className={`ml-1 px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 transition-opacity ${
                              signup.status === 'active' ? 'bg-green-100 text-green-800' : 
                              signup.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                              signup.status === 'inactive' ? 'bg-gray-100 text-gray-800' : 
                              signup.status === 'cancelled' ? 'bg-red-100 text-red-800' : 
                              'bg-gray-100 text-gray-800'
                            } ${hasUnpaidInvoices(signup.participant_name) || signup.status === 'cancelled' ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80 cursor-pointer'}`}
                            title={
                              hasUnpaidInvoices(signup.participant_name) ? 'Cannot toggle - outstanding invoices' : 
                              signup.status === 'active' ? 'Click to deactivate' : 
                              signup.status === 'inactive' ? 'Click to activate' : ''
                            }
                            disabled={hasUnpaidInvoices(signup.participant_name) || signup.status === 'cancelled'}
                          >
                            {signup.status}
                            {signup.status !== 'cancelled' && !hasUnpaidInvoices(signup.participant_name) && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>}
                          </button>
                        </p>
                        <p><strong>Payment:</strong> 
                          <span className={`ml-1 px-2 py-1 rounded-full text-xs font-medium ${
                            signup.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 
                            signup.payment_status === 'not_required' ? 'bg-blue-100 text-blue-800' :
                            signup.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                            signup.payment_status === 'failed' ? 'bg-red-100 text-red-800' : 
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {signup.payment_status === 'not_required' ? 'Invoice-based' : signup.payment_status}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="ml-4 flex flex-col items-end gap-2">
                      <AcademicCapIcon className="h-8 w-8 text-orange-500" />
                      {signup.status !== 'cancelled' && (
                        <button
                          onClick={() => handleCancelSignup(signup)}
                          className={`mt-2 p-2 rounded-lg transition-colors ${
                            hasUnpaidInvoices(signup.participant_name) 
                              ? 'text-red-300 bg-gray-100 cursor-not-allowed' 
                              : 'text-red-600 hover:bg-red-50'
                          }`}
                          title={
                            hasUnpaidInvoices(signup.participant_name) 
                              ? 'Cannot cancel - outstanding invoices' 
                              : 'Permanently cancel this signup (cannot be undone)'
                          }
                          disabled={hasUnpaidInvoices(signup.participant_name)}
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card p-8 text-center">
              <AcademicCapIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Program Registrations Yet</h3>
              <p className="text-gray-600 mb-4">You haven't registered for any programs yet.</p>
              <Link 
                to="/programmes" 
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-300"
              >
                Browse Programs
              </Link>
            </div>
          )}
        </div>

        {/* Program Invoices */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Program Invoices</h2>
          {invoices.length > 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invoices.map((invoice) => (
                      <React.Fragment key={invoice.id}>
                        <tr 
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            const newExpanded = new Set(expandedInvoices);
                            if (newExpanded.has(invoice.id)) {
                              newExpanded.delete(invoice.id);
                            } else {
                              newExpanded.add(invoice.id);
                            }
                            setExpandedInvoices(newExpanded);
                          }}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <div className="text-sm font-medium text-gray-900">{invoice.invoice_number}</div>
                              {(invoice.children_details && invoice.children_details.length > 0) && (
                                <span className="text-xs text-blue-600">({invoice.children_details.length} {invoice.children_details.length > 1 ? 'children' : 'child'})</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{invoice.billing_month}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 capitalize">{invoice.invoice_type || 'monthly'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="text-sm font-semibold text-gray-900">${invoice.amount?.toFixed(2) || '0.00'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              invoice.status === 'paid' ? 'bg-green-100 text-green-800' : 
                              invoice.status === 'unpaid' ? 'bg-yellow-100 text-yellow-800' : 
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {invoice.status || 'unknown'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                            {invoice.status === 'unpaid' ? (
                              <button
                                onClick={() => handlePayInvoice(invoice.id)}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                              >
                                Pay Now
                              </button>
                            ) : (
                              <span className="text-sm text-gray-500">—</span>
                            )}
                          </td>
                        </tr>
                        {expandedInvoices.has(invoice.id) && (
                          <tr>
                            <td colSpan="6" className="px-6 py-4 bg-gray-50">
                              <div className="space-y-4">
                                {invoice.children_details && invoice.children_details.length > 0 ? (
                                  invoice.children_details.map((child, idx) => (
                                    <div key={idx} className="bg-white rounded-lg p-4 border border-gray-200">
                                      <div className="flex justify-between items-start mb-2">
                                        <div>
                                          <p className="font-semibold text-gray-900">{child.participant_name}</p>
                                          <p className="text-sm text-gray-600">{child.program_name}</p>
                                        </div>
                                        <p className="text-sm font-semibold text-gray-900">${child.amount.toFixed(2)}</p>
                                      </div>
                                        <div className="grid grid-cols-2 gap-4 text-xs text-gray-600">
                                          <div>
                                            {child.fee_type && child.fee_type.toLowerCase().includes('term') ? (
                                              <>
                                                <p><strong>Fee Type:</strong> Term Fee (Fixed)</p>
                                                <p><strong>Total Amount:</strong> ${child.amount.toFixed(2)}</p>
                                              </>
                                            ) : (child.fee_type && child.fee_type.toLowerCase().includes('term')) || child.per_lesson_rate > 500 ? (
                                              <>
                                                <p><strong>Fee Type:</strong> Term Fee (Fixed)</p>
                                                <p><strong>Total Amount:</strong> ${child.amount.toFixed(2)}</p>
                                              </>
                                            ) : child.attendance_count > 0 ? (
                                              <>
                                                <p><strong>Attendance:</strong> {child.attendance_count} {child.attendance_count === 1 ? 'session' : 'sessions'}</p>
                                                <p><strong>Rate:</strong> ${child.per_lesson_rate}/session</p>
                                              </>
                                            ) : (
                                              <p><strong>Fee Type:</strong> Fixed rate</p>
                                            )}
                                          </div>
                                          {child.attendance_dates && child.attendance_dates.length > 0 && (
                                            <div>
                                              <p className="font-medium mb-1">Dates Attended:</p>
                                              <p className="text-xs">{child.attendance_dates.map(date => 
                                                new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                              ).join(', ')}</p>
                                            </div>
                                          )}
                                        </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                                    <p className="text-sm text-gray-600">No detailed information available</p>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="card p-8 text-center">
              <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Invoices Yet</h3>
              <p className="text-gray-600">Invoices will appear here once you're enrolled in a program and attendance is recorded.</p>
            </div>
          )}
        </div>

        {/* Recent Orders */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Recent Orders</h2>
          {orders.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {orders.slice(0, 6).map((order) => (
                <div key={order.id} className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {order.product?.name || 'Merchandise Order'}
                      </h3>
                      <div className="space-y-1 text-sm text-gray-600">
                        <p><strong>Order ID:</strong> {order.id}</p>
                        <p><strong>Product:</strong> {order.product?.name || 'Merchandise Item'}</p>
                        <p><strong>Quantity:</strong> {order.quantity}</p>
                        <p><strong>Unit Price:</strong> ${order.unit_price?.toFixed(2) || '0.00'}</p>
                        <p><strong>Total:</strong> ${order.total_price?.toFixed(2) || '0.00'}</p>
                        <p><strong>Status:</strong>
                          <span className={`ml-1 px-2 py-1 rounded-full text-xs font-medium ${
                            order.status === 'paid' ? 'bg-green-100 text-green-800' :
                            order.status === 'pending_payment' ? 'bg-yellow-100 text-yellow-800' :
                            order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            order.status === 'completed' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {order.status === 'pending_payment' ? 'Pending Payment' : 
                             order.status === 'paid' ? 'Paid' :
                             order.status === 'completed' ? 'Completed' :
                             order.status}
                          </span>
                        </p>
                        <p><strong>Date:</strong> {order.timestamp ? new Date(order.timestamp).toLocaleDateString() : 
                          order.created_time ? new Date(order.created_time).toLocaleDateString() : 'N/A'}</p>
                        {order.shipping_address && (
                          <p><strong>Shipping:</strong> {order.shipping_address}</p>
                        )}
                      </div>
                    </div>
                    <div className="ml-4">
                      <ShoppingBagIcon className="h-8 w-8 text-purple-500" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card p-8 text-center">
              <ShoppingBagIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Orders Yet</h3>
              <p className="text-gray-600 mb-4">You haven't made any purchases yet.</p>
              <Link
                to="/merchandise"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
              >
                Shop Merchandise
              </Link>
            </div>
          )}
        </div>
        </div>
      </section>

      {/* Cancel Signup Confirmation Modal */}
      {showCancelModal && signupToCancel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <ExclamationTriangleIcon className="h-10 w-10 text-red-600 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Cancel Signup</h3>
                <p className="text-sm text-gray-500">This will mark the signup as cancelled</p>
              </div>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-700">
                <strong>Program:</strong> {signupToCancel.program_name}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Participant:</strong> {signupToCancel.participant_name}
              </p>
            </div>
            
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to cancel this signup? Your child will no longer be enrolled in this program. Note: You cannot cancel if there are unpaid invoices for this child.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setSignupToCancel(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
              >
                Keep Enrolled
              </button>
              <button
                onClick={confirmCancelSignup}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
              >
                Cancel Signup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
